<script setup lang="ts">
  import Questionaire from '@/components/questionaire/questionaireForm'
  import completedQuestionaire from '@/components/questionaire/completedQuestionaire.vue'
  import { LoaderCircle } from 'lucide-vue-next'
  import client404 from '@/components/404/404.vue'
  import { useClientActionStore } from '@/store/clientActionStore'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { createInstructions, getFormConfig } from '@/lib/utils'
  import type { Item, Page } from '@/types'
  import { genericErrorToast } from '@/lib/toasts'

  const route = useRoute()
  const clientActionsStore = useClientActionStore()
  const accessToken = computed(() => route.query.access_token as string | undefined)
  const isDataLoaded = ref(false)
  const submissionId = computed(
    () => (route.query.short_id as string) || ((route.params.id || route.params.uuid) as string)
  )
  const submissions = computed(() => clientActionsStore.clientAction?.submissions)
  const hasActions = computed(() => clientActionsStore.clientAction !== null)
  const submissionIsCompleted = computed(() =>
    submissions.value?.some(
      (submission) => submission.shortId === submissionId.value && submission.state === 'CLOSED'
    )
  )
  const submissionUrl = computed(() => {
    const submission = submissions.value?.find(
      (submission) => submission.shortId === submissionId.value
    )
    return submission ? submission.pdfUrl : ''
  })
  const baseformConfig = computed(() => {
    if (submissions.value) {
      const config = getFormConfig(submissions.value, submissionId.value)
      return config || { items: [], activeFieldId: '', title: '', totalQuestionCount: 0 }
    }
    return { items: [], activeFieldId: '', title: '', totalQuestionCount: 0 }
  })

  if (submissionId.value) {
    clientActionsStore.setCurrentSubmissionId(submissionId.value)
  }

  watch(submissionId, (newId) => {
    if (newId) {
      clientActionsStore.setCurrentSubmissionId(newId)
    }
  })

  if (accessToken.value) {
    clientActionsStore.setAccessToken(accessToken.value)
  }

  watch(accessToken, (newToken) => {
    if (newToken) {
      clientActionsStore.setAccessToken(newToken)
    }
  })

  const InformationformConfig: Item[] = [
    {
      type: 'information',
      label: 'Information',
      text: createInstructions(clientActionsStore.getFirstName, clientActionsStore.getLastName),
      buttonText: 'Apply here',
      optional: true,
      help: '',
      item: '',
      value: '',
      _errors: '',
      choices: [],
      _choices: '',
      tag: 'information',
      uuid: 'information-uuid',
      jump_to: []
    }
  ]

  const formConfig = computed(() => [...baseformConfig.value.items])
  const hasFormItems = computed(() => formConfig.value.length > 0)
  const metadata = computed(() => ({
    activeField: baseformConfig.value.activeFieldId,
    title: baseformConfig.value.title,
    totalQuestionCount: baseformConfig.value.totalQuestionCount + 1,
    isPreview: false,
    isExternal: true
  }))

  onMounted(async () => {
    try {
      const status = await clientActionsStore.fetchQuestionnaireSubmissionsForExternalView()
      if (status !== 200) {
        genericErrorToast()
      } else {
        isDataLoaded.value = true
      }
    } catch (error) {
      console.error('Error Fetching Questionnaire Submissions:', error)
      genericErrorToast()
    }
  })

  const currentSubmission = computed(() =>
    submissions.value ? submissions.value[0] : { schema_: [] }
  )

  const rawSubmission = computed(() => currentSubmission.value.schema_ as Page[] | [])
</script>

<template>
  <div class="min-h-screen bg-gradient-to-bl from-light-cyan via-white to-light-cyan">
    <!-- Show loading text when data is not yet loaded -->
    <div
      v-if="!isDataLoaded"
      class="flex h-screen items-center justify-center"
    >
      <LoaderCircle
        :size="100"
        class="animate-spin"
      />
    </div>

    <!-- Display components based on the condition -->
    <template v-else>
      <!-- Display completedQuestionaire if the submission is completed -->
      <completedQuestionaire
        v-if="submissionIsCompleted"
        :submission-url="submissionUrl"
      />

      <!-- Display Questionaire if data is loaded, client actions exist, and there are form items -->
      <Questionaire
        v-else-if="hasActions && hasFormItems"
        :formConfig="formConfig"
        :metaData="metadata"
        :rawSchema="rawSubmission"
      />

      <!-- Display client404 if there are no client actions or form items -->
      <client404 v-else />
    </template>
  </div>
</template>
